// Book a course's sessions (spec Q4): the server proposes the course's
// unbooked dates over the next four weeks, each with the free machines of the
// patient's infection group in the chosen shift. The in-charge picks and books;
// every booking is re-checked by the server, and a refusal shows on its row.

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { bookSession, fetchProposal } from "../api/daycareApi";
import { courseSummary, groupLabel } from "../utils/sessionView";
import { formatIstDate } from "../../../utils/istDateTime";
import { IPD_COLORS, IpdButton, IpdFormField, IpdTextField } from "../../ipd/utils";

export default function BookCourseDialog({ order, unit, onClose, onBooked }) {
  const [shiftCode, setShiftCode] = useState(order.preferred_shift);
  const [proposal, setProposal] = useState(null);
  const [picks, setPicks] = useState({});
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (shift) => {
    setLoading(true);
    try {
      const data = await fetchProposal(order.id, shift);
      setProposal(data);
      setPicks(Object.fromEntries((data.dates || []).map((row) => [
        row.date, { selected: Boolean(row.suggested_machine_id), machineId: row.suggested_machine_id || "" },
      ])));
    } catch (err) {
      toast.error(err?.body?.message || "Could not load the course's dates");
      setProposal({ dates: [] });
    } finally {
      setLoading(false);
    }
  }, [order.id]);

  useEffect(() => { load(shiftCode); }, [load, shiftCode]);

  const setPick = (date, patch) => setPicks((p) => ({ ...p, [date]: { ...p[date], ...patch } }));

  const book = async () => {
    setBusy(true);
    const outcome = {};
    let booked = 0;
    // One at a time: each booking takes the machine's row lock on the server.
    for (const row of proposal.dates) {
      const pick = picks[row.date];
      if (!pick?.selected || !pick.machineId) continue;
      // eslint-disable-next-line no-await-in-loop
      const res = await bookSession({
        order_id: order.id, session_date: row.date, shift_code: shiftCode, machine_id: pick.machineId,
      });
      outcome[row.date] = res.ok ? "Booked" : res.message || "Could not book";
      if (res.ok) booked += 1;
    }
    setBusy(false);
    setResults(outcome);
    if (booked) {
      toast.success(`${booked} session${booked > 1 ? "s" : ""} booked`);
      onBooked();
      load(shiftCode);
    }
  };

  const selectedCount = Object.values(picks).filter((p) => p.selected && p.machineId).length;

  const headSx = { fontSize: 11.5, fontWeight: 700, color: IPD_COLORS.textSecondary, textTransform: "uppercase",
    letterSpacing: 0.6, bgcolor: IPD_COLORS.panelHeaderBg };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>{`Book sessions — ${order.patient_name || order.patient_id}`}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end", flexWrap: "wrap", mb: 2 }}>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography sx={{ fontSize: 13, color: IPD_COLORS.textPrimary }}>{courseSummary(order)}</Typography>
            <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
              {`Machines: ${groupLabel(proposal?.patient_group || order.patient_group)}${proposal?.retest_due ? " · markers retest due" : ""}`}
            </Typography>
          </Box>
          <IpdFormField label="Shift" sx={{ minWidth: 220 }}>
            <IpdTextField select fullWidth value={shiftCode}
              onChange={(e) => { setResults({}); setShiftCode(e.target.value); }}>
              {(unit?.shifts || []).map((s) => (
                <MenuItem key={s.code} value={s.code}>{`${s.label} (${s.start}-${s.end})`}</MenuItem>
              ))}
            </IpdTextField>
          </IpdFormField>
        </Box>

        {loading ? (
          <Box sx={{ py: 3, display: "grid", placeItems: "center" }}><CircularProgress size={22} /></Box>
        ) : !proposal?.dates?.length ? (
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, py: 2 }}>
            Nothing left to book in the next four weeks for this course.
          </Typography>
        ) : (
          <Box sx={{ border: `1px solid ${IPD_COLORS.border}`, borderRadius: 1, overflow: "hidden" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={headSx} />
                  <TableCell sx={headSx}>Date</TableCell>
                  <TableCell sx={headSx}>Machine</TableCell>
                  <TableCell sx={headSx}>Result</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {proposal.dates.map((row) => {
                  const pick = picks[row.date] || {};
                  const result = results[row.date];
                  return (
                    <TableRow key={row.date}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={Boolean(pick.selected)}
                          disabled={!row.free_machines.length}
                          onChange={(e) => setPick(row.date, { selected: e.target.checked })}
                          inputProps={{ "aria-label": `Book ${row.date}` }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{formatIstDate(row.date)}</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        {row.free_machines.length ? (
                          <IpdTextField select fullWidth value={pick.machineId || ""}
                            inputProps={{ "aria-label": `Machine for ${row.date}` }}
                            onChange={(e) => setPick(row.date, { machineId: e.target.value, selected: true })}>
                            {row.free_machines.map((m) => <MenuItem key={m.id} value={m.id}>{m.bed_name}</MenuItem>)}
                          </IpdTextField>
                        ) : (
                          <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.warning.text }}>
                            No free machine of this group in this shift
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12.5, color: result === "Booked" ? IPD_COLORS.success.text : IPD_COLORS.error.text }}>
                        {result || ""}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <IpdButton variant="outlined" onClick={onClose}>Close</IpdButton>
        <IpdButton loading={busy} disabled={busy || !selectedCount} onClick={book}>
          {`Book ${selectedCount} session${selectedCount === 1 ? "" : "s"}`}
        </IpdButton>
      </DialogActions>
    </Dialog>
  );
}
